import { Link } from "@/i18n/Link";
import { ArrowRight, Award, Phone, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { SITE_PHONE_DISPLAY } from "@/routes-data";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    home: "Home",
    distributor: "Authorized Dexter Distributor",
    seoTitle: "Authorized Dexter Axle Distributor in Elkhart, IN",
    seoDesc: "Axle Inc. is the Elkhart-area's exclusive Dexter Group distributor. Genuine Dexter axles, hubs, brakes, and suspension stocked locally for northern Indiana trailers.",
    badge: "Authorized Distributor",
    heroTitle: "The local face of Dexter Group.",
    heroDesc: "Axle Inc. is the Elkhart-area's exclusive Dexter Group distributor. That means full access to Dexter's complete catalog of axles, brakes, suspension, and components - backed by local stock, local service, and a phone that gets answered.",
    shopParts: "Shop Dexter parts",
    whyMatters: "Why this matters",
    whyText1: "Dexter Group is the largest North American manufacturer of trailer axles, brake systems, and components. If you've owned a trailer in the last forty years, statistically you've ridden on Dexter parts - they're the OEM standard for most major trailer manufacturers.",
    whyText2: "Being an authorized distributor isn't a marketing label. It means we're an extension of Dexter for the Elkhart area: we carry their full catalog at distributor pricing, we get their engineering support, we honor their warranties, and we participate in their customer rebates. When you buy a Dexter axle from Axle Inc., you're getting the same part you'd get from the factory - just faster, with someone you can call.",
    lineTitle: "The Dexter line, in stock locally.",
    lineDesc: "We carry every category Dexter makes - and we keep the high-volume parts on the shelf so most repairs are same-day or next-day.",
    lines: [
      { title: "Trailer Axles", desc: "Torflex, leaf-spring, and rubber-torsion axles from 1,000 to 10,000 lb capacity. Built to spec, branded Dexter, stocked locally." },
      { title: "Brake Systems", desc: "Electric drum (Nev-R-Adjust and standard), hydraulic drum, and hydraulic disc. Plus actuators, controllers, and complete brake assemblies." },
      { title: "Suspension", desc: "Leaf spring suspension, hangers, equalizers, and hardware for single, tandem, and triple-axle systems." },
      { title: "Hubs & Drums", desc: "Idler hubs, hub-and-drum assemblies, full hub-bearing-seal kits - Dexter OEM, the same parts the trailer left the factory with." },
      { title: "Ag & Utility Components", desc: "Heavy-duty axles and components for agricultural, utility, and commercial applications. Built for working trailers." },
      { title: "Replacement Parts", desc: "Bearings, seals, magnets, shoes, springs, hardware - Dexter genuine, with full part-number traceability." }
    ],
    builtFor: "Built for everyone - from weekend trailer owner to OEM",
    builtText: "Whether you've got a 12-foot utility trailer that needs new bearings or you're a manufacturer building 200 trailers a quarter, the Dexter catalog has the right component. Our job is connecting you to it without a six-week lead time.",
    support: "Rebates, warranty, and engineering support",
    supportText1: "Dexter runs ongoing customer rebates on axles and brake assemblies. As an authorized distributor, we process those rebates directly - you don't chase them. Dexter also stands behind their components with industry-leading warranties, and we handle warranty claims locally so you're not on the phone with a national support queue.",
    supportText2: "Need engineering input? Spec'ing a custom axle or brake configuration? We have direct lines to Dexter's engineering team and can get application-specific recommendations turned around fast.",
    btnRebates: "Current rebates",
    btnContact: "Talk to our team",
    btnDexter: "Visit dextergroup.com →"
  },
  es: {
    home: "Inicio",
    distributor: "Distribuidor Autorizado Dexter",
    seoTitle: "Distribuidor Autorizado de Ejes Dexter en Elkhart, IN",
    seoDesc: "Axle Inc. es el distribuidor exclusivo de Dexter Group en el área de Elkhart. Ejes, mazas, frenos y suspensiones originales Dexter en inventario local para remolques del norte de Indiana.",
    badge: "Distribuidor Autorizado",
    heroTitle: "La cara local de Dexter Group.",
    heroDesc: "Axle Inc. es el distribuidor exclusivo de Dexter Group en el área de Elkhart. Esto significa acceso total al catálogo completo de Dexter de ejes, frenos, suspensiones y componentes, respaldado por inventario local, servicio local y una línea telefónica donde sí le contestamos.",
    shopParts: "Comprar partes Dexter",
    whyMatters: "Por qué es importante",
    whyText1: "Dexter Group es el mayor fabricante norteamericano de ejes para remolque, sistemas de frenos y componentes. Si ha tenido un remolque en los últimos cuarenta años, estadísticamente usted ha usado partes Dexter; son el estándar OEM para la mayoría de los principales fabricantes de remolques.",
    whyText2: "Ser un distribuidor autorizado no es solo una etiqueta de marketing. Significa que somos una extensión de Dexter para el área de Elkhart: tenemos su catálogo completo a precios de distribuidor, recibimos su soporte técnico, honramos sus garantías y participamos en sus reembolsos para clientes. Cuando compra un eje Dexter en Axle Inc., obtiene la misma parte que recibiría de fábrica, solo que más rápido y con alguien a quien puede llamar.",
    lineTitle: "La línea Dexter, en inventario local.",
    lineDesc: "Manejamos todas las categorías que fabrica Dexter y mantenemos las partes de alta rotación en estante para que la mayoría de las reparaciones sean el mismo día o al día siguiente.",
    lines: [
      { title: "Ejes para Remolque", desc: "Ejes Torflex, de ballesta y de torsión de caucho con capacidad de 1,000 a 10,000 lb. Construidos según especificaciones, marca Dexter, en inventario local." },
      { title: "Sistemas de Frenos", desc: "Frenos de tambor eléctricos (Nev-R-Adjust y estándar), frenos de tambor hidráulicos y frenos de disco hidráulicos. Además de actuadores, controladores y ensambles de freno completos." },
      { title: "Suspensiones", desc: "Suspensión de ballesta, soportes, igualadores y herrajes para sistemas de uno, dos y tres ejes." },
      { title: "Mazas y Tambores", desc: "Mazas locas, ensambles de maza y tambor, kits completos de maza-rodamientos-sellos: OEM de Dexter, las mismas partes con las que el remolque salió de fábrica." },
      { title: "Componentes Agrícolas y de Utilidad", desc: "Ejes de servicio pesado y componentes para aplicaciones agrícolas, de utilidad y comerciales. Construidos para remolques de trabajo." },
      { title: "Partes de Repuesto", desc: "Rodamientos, sellos, imanes, balatas, resortes, herrajes: legítimos de Dexter, con trazabilidad total de número de parte." }
    ],
    builtFor: "Construido para todos: desde el dueño de un remolque de fin de semana hasta OEM",
    builtText: "Ya sea que tenga un remolque de utilidad de 12 pies que necesite rodamientos nuevos o sea un fabricante que construye 200 remolques por trimestre, el catálogo de Dexter tiene el componente correcto. Nuestro trabajo es conectarlo con él sin tiempos de espera de seis semanas.",
    support: "Reembolsos, garantía y soporte técnico",
    supportText1: "Dexter tiene reembolsos continuos para clientes en ejes y ensambles de freno. Como distribuidor autorizado, procesamos esos reembolsos directamente; usted no tiene que perseguirlos. Dexter también respalda sus componentes con garantías líderes en la industria, y manejamos los reclamos de garantía localmente para que usted no tenga que hablar con una cola de soporte nacional.",
    supportText2: "¿Necesita asesoría técnica? ¿Está especificando una configuración personalizada de eje o frenos? Tenemos líneas directas con el equipo de ingeniería de Dexter y podemos obtener recomendaciones específicas para su aplicación rápidamente.",
    btnRebates: "Reembolsos actuales",
    btnContact: "Hable con nuestro equipo",
    btnDexter: "Visite dextergroup.com (en inglés) →"
  }
};

export default function Dexter() {
  const t = useCopy(COPY);
  const trail = [
    { name: t.home, path: "/" },
    { name: t.distributor, path: "/dexter/" },
  ];
  return (
    <PageLayout>
      <Seo
        title={t.seoTitle}
        description={t.seoDesc}
        path="/dexter/"
        jsonLd={[organizationJsonLd(), breadcrumbJsonLd(trail)]}
      />
      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={trail} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-white font-semibold mb-4 inline-flex items-center gap-2">
            <Award className="h-4 w-4" /> {t.badge}
          </p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">
            {t.heroTitle}
          </h1>
          <p className="mt-5 text-lg text-secondary-foreground/80 max-w-2xl leading-relaxed">
            {t.heroDesc}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <a href="https://parts.axleinc.com/" target="_blank" rel="noopener">{t.shopParts} <ArrowRight className="ml-1.5 h-4 w-4" /></a>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10">
              <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                <Phone className="mr-1.5 h-4 w-4" /> {SITE_PHONE_DISPLAY}
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20 max-w-4xl">
        <div className="prose prose-neutral max-w-none">
          <h2>{t.whyMatters}</h2>
          <p>{t.whyText1}</p>
          <p>{t.whyText2}</p>
        </div>
      </section>

      <section className="bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3 max-w-2xl">{t.lineTitle}</h2>
          <p className="text-muted-foreground max-w-2xl mb-10 leading-relaxed">
            {t.lineDesc}
          </p>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {t.lines.map((line) => (
              <div key={line.title} className="bg-card border border-border rounded-md p-6">
                <Truck className="h-5 w-5 text-primary mb-3" />
                <h3 className="font-semibold mb-2">{line.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{line.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20 max-w-4xl">
        <div className="prose prose-neutral max-w-none">
          <h2>{t.builtFor}</h2>
          <p>{t.builtText}</p>
          <h2>{t.support}</h2>
          <p>{t.supportText1}</p>
          <p>{t.supportText2}</p>
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/rebates/">{t.btnRebates} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/contact/">{t.btnContact}</Link>
          </Button>
          <Button asChild variant="outline">
            <a href="https://www.dextergroup.com/" target="_blank" rel="noopener">{t.btnDexter}</a>
          </Button>
        </div>
      </section>
    </PageLayout>
  );
}
